<template>
  <v-dialog
    v-model="dialog"
    max-width="800"
  >
    <template #activator="{ props }">
      <v-badge
        v-if="hasNewVersion"
        color="error"
        content="NEW"
        location="top right"
        :offset-x="10"
        :offset-y="16"
      >
        <v-btn
          v-bind="props"
          variant="text"
          :icon="mdiTextBoxOutline"
        />
      </v-badge>
      <v-btn
        v-else
        v-bind="props"
        variant="text"
        :icon="mdiTextBoxOutline"
      />
    </template>

    <v-card>
      <v-card-title class="d-flex align-center bg-primary">
        <v-icon
          :icon="mdiTextBoxOutline"
          class="mr-2"
        />
        {{ t('changelog.title') }}
        <v-spacer />
        <v-btn
          icon
          variant="text"
          @click="closeDialog"
        >
          <v-icon>{{ mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text
        class="pt-4"
        style="max-height: 600px; overflow-y: auto"
      >
        <v-timeline
          side="end"
          density="compact"
        >
          <v-timeline-item
            v-for="entry in changelog"
            :key="entry.version"
            dot-color="primary"
            size="small"
          >
            <v-card class="mb-4">
              <v-card-title class="text-h6 bg-surface d-flex align-center flex-wrap">
                <span>Version {{ entry.version }}</span>
                <v-spacer />
                <v-chip
                  size="small"
                  variant="outlined"
                  class="ml-2"
                >
                  {{ entry.date }}
                </v-chip>
              </v-card-title>

              <!-- Release description -->
              <v-card-text
                v-if="entry.description"
                class="pt-3 pb-2"
              >
                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="text-body-2"
                >
                  {{ getTranslatedText(entry.description) }}
                </v-alert>
              </v-card-text>

              <v-card-text :class="entry.description ? 'pt-0' : ''">
                <v-list density="compact">
                  <v-list-item
                    v-for="(change, index) in entry.changes"
                    :key="index"
                    :class="getChangeClass(change.type)"
                  >
                    <template #prepend>
                      <v-icon
                        :icon="getChangeIcon(change.type)"
                        class="mr-2"
                      />
                    </template>
                    <v-list-item-title class="d-flex align-start">
                      <v-chip
                        size="x-small"
                        class="mr-2 flex-shrink-0"
                        :color="getChangeColor(change.type)"
                      >
                        {{ t(`changelog.types.${change.type}`) }}
                      </v-chip>
                      <span class="text-wrap flex-grow-1">
                        {{ getTranslatedText(change.description) }}
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          @click="closeDialog"
        >
          {{ t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { changelog } from '@/data/changelog';
import { useAnalytics } from '@/composables/useAnalytics';
import {
  mdiTextBoxOutline,
  mdiClose,
  mdiNewBox,
  mdiBugOutline,
  mdiTrendingUp,
  mdiAlertCircleOutline,
} from '@mdi/js';

const LAST_SEEN_VERSION_KEY = 'vicoma-app-last-seen-version';

const { t, locale } = useI18n();
const { trackChangelogViewed } = useAnalytics();
const dialog = ref(false);

// Get the latest version from changelog
const latestVersion = computed(() => changelog[0]?.version || '0.0.0');

// Check if there's a new version
const hasNewVersion = ref(false);

const checkForNewVersion = () => {
  const lastSeenVersion = localStorage.getItem(LAST_SEEN_VERSION_KEY);

  if (!lastSeenVersion) {
    // First time user - mark as new version
    hasNewVersion.value = true;
    return true;
  }

  // Compare versions - if different, it's a new version
  if (lastSeenVersion !== latestVersion.value) {
    hasNewVersion.value = true;
    return true;
  }

  hasNewVersion.value = false;
  return false;
};

const markVersionAsSeen = () => {
  localStorage.setItem(LAST_SEEN_VERSION_KEY, latestVersion.value);
  hasNewVersion.value = false;
};

const closeDialog = () => {
  dialog.value = false;
  markVersionAsSeen();
};

// Expose method to open dialog programmatically
const openDialog = () => {
  dialog.value = true;
  trackChangelogViewed();
};

defineExpose({ openDialog });

onMounted(() => {
  // Check if there's a new version
  const isNewVersion = checkForNewVersion();

  // Auto-open dialog if new version detected
  if (isNewVersion) {
    // Small delay to ensure everything is loaded
    setTimeout(() => {
      dialog.value = true;
    }, 1000);
  }
});

const getChangeIcon = (type: string) => {
  switch (type) {
    case 'feature':
      return mdiNewBox;
    case 'bugfix':
      return mdiBugOutline;
    case 'improvement':
      return mdiTrendingUp;
    case 'breaking':
      return mdiAlertCircleOutline;
    default:
      return mdiNewBox;
  }
};

const getChangeColor = (type: string) => {
  switch (type) {
    case 'feature':
      return 'success';
    case 'bugfix':
      return 'error';
    case 'improvement':
      return 'info';
    case 'breaking':
      return 'warning';
    default:
      return 'primary';
  }
};

const getChangeClass = (type: string) => {
  return `change-${type}`;
};

// Helper function to get translated text
const getTranslatedText = (text: string | { en: string; nl: string }) => {
  if (typeof text === 'string') return text;
  return locale.value === 'en' ? text.en : text.nl;
};
</script>

<style scoped>
.change-feature {
  color: rgb(var(--v-theme-success));
}

.change-bugfix {
  color: rgb(var(--v-theme-error));
}

.change-improvement {
  color: rgb(var(--v-theme-info));
}

.change-breaking {
  color: rgb(var(--v-theme-warning));
}
</style>
